/**
 * Patchwerk - Audio/MIDI Routing Visualizer
 * 
 * This file contains ONLY runtime functions and business logic.
 * All initialization is handled by Bootstrap.js
 */

// ============================================================================
// Imports
// ============================================================================

import { generateId, escapeHtml } from './utils/index.js';
import { deepClone } from './utils/helpers.js';
import { appState } from './services/AppState.js';
import {
    PATCH_VERSION, createPatchData, validatePatchData, remapPatchIds,
    toShareString, fromShareString
} from './utils/serialization.js';
import {
    APP_VERSION, STORAGE_KEY, VIEWPORT_STORAGE_KEY, CURRENT_PATCH_STORAGE_KEY,
    HISTORY_MAX_SIZE, PORT_TYPE_DISPLAY_NAMES
} from './constants/index.js';
import { getConnectionRules } from './config/connectionRules.js';
import { historyService, createConnectionRestoreFunctions } from './services/UndoManager.js';
import {
    normalizeSignalTypes, getGroupByRef as getGroupByRefService,
    getGroupIndex as getGroupIndexService, getGroupFromDevice as getGroupFromDeviceService,
    resolveGroupIndex as resolveGroupIndexService
} from './services/ConnectionService.js';
import { migrateDevice, migrateConnections } from './services/MigrationService.js';
import { eventBus, EVENTS } from './services/EventBus.js';
import { cableIntersectsBox as cableIntersectsBoxGeom, calculateControlPoints } from './utils/geometry.js';
import { createDeviceElement } from './renderers/DeviceRenderer.js';
import { createDeviceService } from './services/DeviceService.js';
import { findOpenPosition } from './services/LayoutService.js';
import { bootstrap } from './Bootstrap.js';
import { createDemoPatch } from './demo/DemoPatch.js';
import {
    saveDevice, saveDeviceAsNew, addPortGroupRow, removePortGroup, updatePortGroup,
    toggleGroupAdvanced, showMultiTypeEditor, closeMultiTypeEditor, applyMultiTypeEditor,
    applyMultiTypePreset, toggleMultiType
} from './controllers/DeviceModalController.js';

// ============================================================================
// Runtime State (populated by bootstrap)
// ============================================================================

let state, instanceModel, definitionModel, connectionModel, debugConfig;
let authService, storageService, favoritesService, patchSettingsService;
let selectionState, libraryMultiSelect, PORT_CONNECTION_RULES;
let canvas, cablesSvg;

// Controller references
let controllers = {};

// Re-exports from Bootstrap
let renderAllCables, updateConnectedPorts, getViewport, applyViewportTransform;
let updateZoomIndicator, fitAllDevices, saveViewport, screenToCanvas;
let getPortPosition, getConnectionSourceGroup, getConnectionTargetGroup;
let clearDeviceSelection, setDeviceSelection, hasDeviceSelection, getSingleSelectedDevice;
let getSelectedDevices, updateSelectionVisuals, bringToFront;
let showToast, showConfirmModal, showAddDeviceModal, showEditTemplateModal, closeModal;
let getCableDragState, getLastMousePosition, getLastHoveredTargetPortInfo, updateTempCable;
let deleteSelected, clearRouting, copySelection, pasteSelection;
let getSelectionClipboard, hasSelectionClipboard, hasClipboardContent, pasteDevice, duplicateDevice;
let copyDeviceToSystemClipboard, copyLibrarySelectionToClipboard;
let pasteDeviceFromSystemClipboard, copySelectionToSystemClipboard, pasteSelectionFromSystemClipboard;
let toggleAccountMenu, togglePatchSwitcher, closePatchSwitcher;
let toggleDebugPanel, updateDebugStraightDist, updateDebugCableStyle;
let updatePortRule, deletePortType, exportDebugSettings, contextMenuAction;

// Forward declarations for circular references
let renderLibrary, duplicateSelectedTemplate;
let deviceService = null;

// ============================================================================
// Runtime Functions
// ============================================================================

function getTemplateForDevice(device) {
    return definitionModel.getForDevice(device);
}

function getPortTypeDisplayName(portType) {
    return PORT_TYPE_DISPLAY_NAMES[portType] || portType.charAt(0).toUpperCase() + portType.slice(1);
}

function getGroupDisplayName(group) {
    if (group.name && group.name.trim()) return group.name;
    let primaryType = 'audio';
    if (group.type) {
        primaryType = Array.isArray(group.type) ? group.type[0] : group.type;
    }
    return getPortTypeDisplayName(primaryType);
}

function getConnectionRulesLocal(portType) {
    return getConnectionRules(portType, PORT_CONNECTION_RULES);
}

function checkConnectionAllowed(sourceDeviceId, sourceGroupIndex, sourcePortIndex, sourceType,
                                 targetDeviceId, targetGroupIndex, targetPortIndex) {
    const rules = getConnectionRulesLocal(sourceType);
    const conflictingConnections = [];
    
    if (!rules.fanOut) {
        const sourceConnections = connectionModel.filter(c => {
            const srcGroup = getConnectionSourceGroup(c);
            return c.sourceDeviceId === sourceDeviceId &&
                srcGroup === sourceGroupIndex &&
                c.sourcePortIndex === sourcePortIndex;
        });
        conflictingConnections.push(...sourceConnections);
    }
    
    if (!rules.fanIn) {
        const targetConnections = connectionModel.filter(c => {
            const tgtGroup = getConnectionTargetGroup(c);
            return c.targetDeviceId === targetDeviceId &&
                tgtGroup === targetGroupIndex &&
                c.targetPortIndex === targetPortIndex;
        });
        for (const conn of targetConnections) {
            if (!conflictingConnections.some(c => c.id === conn.id)) {
                conflictingConnections.push(conn);
            }
        }
    }
    
    return { allowed: true, conflictingConnections, reason: null, blockedPort: null };
}

// ============================================================================
// Device Service Wrapper
// ============================================================================

function getDeviceService() {
    if (!deviceService) {
        deviceService = createDeviceService({
            definitionModel,
            instanceModel,
            renderLibrary: () => renderLibrary?.(),
            renderDevice,
            selectDevice,
            scheduleAutosave,
            recordAction
        });
    }
    return deviceService;
}

function createTemplate(name, portGroups, manufacturer = '', version = '', isRouter = false) {
    return getDeviceService().createTemplate(name, portGroups, manufacturer, version, isRouter);
}

function createInstanceFromTemplate(templateId, position, skipHistory = false) {
    return getDeviceService().createInstanceFromTemplate(templateId, position, skipHistory);
}

function createDevice(name, portGroups, position, manufacturer = '', version = '', isRouter = false) {
    return getDeviceService().createDevice(name, portGroups, position, manufacturer, version, isRouter);
}

// ============================================================================
// Device Rendering
// ============================================================================

function renderDevice(device) {
    const existing = document.getElementById(device.id);
    if (existing) existing.remove();

    const template = getTemplateForDevice(device);
    if (!template) {
        console.warn('No template found for device:', device.id);
        return;
    }

    const groupHasConnections = (deviceId, groupIndex, direction) => {
        return connectionModel.some(c => {
            if (direction === 'output') {
                const srcGroup = getConnectionSourceGroup(c);
                return c.sourceDeviceId === deviceId && srcGroup === groupIndex;
            } else {
                const tgtGroup = getConnectionTargetGroup(c);
                return c.targetDeviceId === deviceId && tgtGroup === groupIndex;
            }
        });
    };

    const instanceCount = instanceModel.getByTemplateId(device.templateId).length;
    const el = createDeviceElement(device, template, groupHasConnections, instanceCount);
    canvas.appendChild(el);
    bringToFront(el);
    updateConnectedPorts();
}

function rerenderAllDevicesForTemplate(templateId) {
    instanceModel.getByTemplateId(templateId).forEach(d => renderDevice(d));
    renderAllCables();
    updateConnectedPorts();
}

// ============================================================================
// Selection
// ============================================================================

function selectDevice(deviceId) {
    if (deviceId && selectionState.devices.has(deviceId) && selectionState.devices.size > 1) {
        const el = document.getElementById(deviceId);
        if (el) bringToFront(el);
        return;
    }
    
    if (deviceId) {
        libraryMultiSelect.selectedItems.clear();
        libraryMultiSelect.lastClickedId = null;
    }
    
    state.selectedConnectionId = null;
    state.selectedTemplateId = null;
    
    if (deviceId) {
        setDeviceSelection(deviceId);
        const el = document.getElementById(deviceId);
        if (el) bringToFront(el);
    } else {
        clearDeviceSelection();
    }
    
    updateSelectionButtons();
    renderLibrary?.();
}

function updateSelectionButtons() {
    const definitionBtn = document.getElementById('definition-btn');
    const layoutBtn = document.getElementById('layout-btn');
    const duplicateBtn = document.getElementById('duplicate-btn');
    const copyBtn = document.getElementById('copy-btn');
    const pasteBtn = document.getElementById('paste-btn');
    const deleteBtn = document.getElementById('delete-btn');
    const clearRoutingBtn = document.getElementById('clear-routing-btn');
    
    const deviceSelected = hasDeviceSelection();
    const singleDeviceSelected = getSingleSelectedDevice() !== null;
    const hasConnectionSelected = state.selectedConnectionId !== null;
    const hasTemplateSelected = state.selectedTemplateId !== null;
    const hasLibraryMultiSelect = libraryMultiSelect.selectedItems.size > 0;
    
    if (definitionBtn) definitionBtn.disabled = !(singleDeviceSelected || hasTemplateSelected || hasLibraryMultiSelect);
    if (layoutBtn) layoutBtn.disabled = !singleDeviceSelected;
    if (duplicateBtn) duplicateBtn.disabled = !(deviceSelected || hasTemplateSelected || hasLibraryMultiSelect);
    if (copyBtn) copyBtn.disabled = !(deviceSelected || hasTemplateSelected || hasLibraryMultiSelect);
    if (pasteBtn) pasteBtn.disabled = !hasClipboardContent();
    if (deleteBtn) deleteBtn.disabled = !(deviceSelected || hasConnectionSelected || hasTemplateSelected || hasLibraryMultiSelect);
    if (clearRoutingBtn) clearRoutingBtn.disabled = !deviceSelected;
}

function editInstanceLayout() {
    const singleDevice = getSingleSelectedDevice();
    if (singleDevice) {
        const device = instanceModel.getById(singleDevice);
        if (device) {
            controllers.instanceLayoutController?.showInstanceLayoutModal(device);
        }
    }
}

function editDefinition() {
    const singleDevice = getSingleSelectedDevice();
    if (singleDevice) {
        const device = instanceModel.getById(singleDevice);
        if (device) {
            const template = definitionModel.getForDevice(device);
            if (template) showEditTemplateModal(template);
        }
    } else if (state.selectedTemplateId) {
        const template = definitionModel.getById(state.selectedTemplateId);
        if (template) showEditTemplateModal(template);
    } else if (libraryMultiSelect.selectedItems.size === 1) {
        const templateId = Array.from(libraryMultiSelect.selectedItems)[0];
        const template = definitionModel.getById(templateId);
        if (template) showEditTemplateModal(template);
    }
}

// ============================================================================
// Undo/Redo
// ============================================================================

function recordAction(type, description, undoFn, redoFn) {
    historyService.recordAction(type, description, undoFn, redoFn);
}

function undo() {
    const result = historyService.undo();
    if (result.success) {
        showToast(`Undo: ${result.description}`);
    } else {
        showToast(result.error, true);
    }
    return result.success;
}

function redo() {
    const result = historyService.redo();
    if (result.success) {
        showToast(`Redo: ${result.description}`);
    } else {
        showToast(result.error, true);
    }
    return result.success;
}

function updateUndoRedoButtons() {
    const undoBtn = document.getElementById('undo-btn');
    const redoBtn = document.getElementById('redo-btn');
    
    if (undoBtn) {
        undoBtn.disabled = !historyService.canUndo();
        const undoDesc = historyService.getUndoDescription();
        undoBtn.title = undoDesc ? `Undo: ${undoDesc} (Ctrl+Z)` : 'Nothing to undo (Ctrl+Z)';
    }
    if (redoBtn) {
        redoBtn.disabled = !historyService.canRedo();
        const redoDesc = historyService.getRedoDescription();
        redoBtn.title = redoDesc ? `Redo: ${redoDesc} (Ctrl+Y)` : 'Nothing to redo (Ctrl+Y)';
    }
}

function clearHistory() {
    historyService.clear();
}

// ============================================================================
// Autosave
// ============================================================================

function scheduleAutosave() {
    storageService?.scheduleAutosave();
}

function autosave() {
    storageService?.triggerImmediateAutosave();
}

function showAutosaveIndicator() {
    const indicator = document.getElementById('autosave-indicator');
    if (indicator) {
        indicator.classList.add('visible');
        setTimeout(() => indicator.classList.remove('visible'), 1000);
    }
}

// ============================================================================
// Patch Management
// ============================================================================

function setCurrentPatchName(name) {
    const patchName = name || 'Untitled Patch';
    appState.set('patch.name', patchName);
    const display = document.getElementById('patch-name-display');
    if (display) display.textContent = patchName;
    try {
        localStorage.setItem(CURRENT_PATCH_STORAGE_KEY, patchName);
    } catch (e) {
        console.warn('Failed to save current patch name:', e);
    }
}

function createNewPatch(patchName) {
    definitionModel.clear();
    instanceModel.clear();
    connectionModel.clear();
    clearDeviceSelection();
    state.selectedConnectionId = null;
    state.selectedTemplateId = null;
    libraryMultiSelect.selectedItems.clear();
    libraryMultiSelect.lastClickedId = null;
    clearHistory();
    canvas.innerHTML = '';
    renderAllCables();
    
    const viewport = getViewport();
    viewport.x = 0;
    viewport.y = 0;
    viewport.scale = 1;
    applyViewportTransform();
    updateZoomIndicator();
    updateSelectionButtons();
    renderLibrary?.();
    
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(VIEWPORT_STORAGE_KEY);
    
    patchSettingsService?.applyPatchSettings(null);
    
    setCurrentPatchName(patchName);
    storageService?.clearCurrentPatchId();
    
    showToast(`New patch created: ${patchName}`);
}

function newPatch() {
    controllers.patchModalController?.showNewPatchModal();
}

// ============================================================================
// Patch Data Application
// ============================================================================

function applyPatchData(patchData, merge = false) {
    const validation = validatePatchData(patchData);
    if (!validation.valid || validation.needsMigration) return false;

    if (!merge) {
        definitionModel.clear();
        instanceModel.clear();
        connectionModel.clear();
        clearDeviceSelection();
        state.selectedConnectionId = null;
        state.selectedTemplateId = null;
        clearHistory();
        canvas.innerHTML = '';

        const viewport = getViewport();
        viewport.x = 0;
        viewport.y = 0;
        viewport.scale = 1;
        applyViewportTransform();
        
        patchSettingsService?.applyPatchSettings(patchData.settings);
    }

    if (merge) {
        const remapped = remapPatchIds(patchData);
        remapped.templates.forEach(t => definitionModel.add(t));
        remapped.devices.forEach(d => {
            instanceModel.add(d);
            renderDevice(d);
        });
        remapped.connections.forEach(c => connectionModel.add(c));
    } else {
        definitionModel.replaceAll(patchData.templates);
        patchData.devices.forEach(d => {
            instanceModel.add(d);
            renderDevice(d);
        });
        if (patchData.connections) {
            connectionModel.replaceAll(patchData.connections);
        }
    }

    renderAllCables();
    updateConnectedPorts();
    renderLibrary?.();
    
    if (!merge && instanceModel.getCount() > 0) {
        requestAnimationFrame(() => fitAllDevices());
    }
    
    return true;
}

async function loadAndApplyPatch(identifier, merge = false) {
    const result = await storageService.loadPatch(identifier);
    if (!result.success || !result.data) return false;

    const success = applyPatchData(result.data, merge);
    if (success && !merge) {
        storageService.setCurrentPatchId(result.id);
    }
    
    if (success) {
        eventBus.emit(EVENTS.PATCH_LOADED, { patchName: result.name || identifier });
    }
    
    return success;
}

// ============================================================================
// Version Management
// ============================================================================

async function loadVersion(versionId) {
    const result = await storageService.loadVersion(versionId);
    if (!result.success || !result.data) {
        showToast('Version not found', true);
        return false;
    }
    
    const success = applyPatchData(result.data, false);
    if (success) {
        autosave();
        showToast(`Restored: "${result.name}"`);
    } else {
        showToast('Failed to restore version', true);
    }
    
    return success;
}

// ============================================================================
// Working Patch Loading
// ============================================================================

async function loadWorkingPatch() {
    try {
        const result = await storageService.loadWorkingPatch();
        if (!result.success || !result.data) return false;
        
        const data = result.data;
        
        if (data.version >= 3 && data.templates) {
            definitionModel.replaceAll(data.templates);
            data.devices.forEach(d => {
                instanceModel.add(d);
                renderDevice(d);
            });
            if (data.connections) {
                connectionModel.replaceAll(data.connections);
                renderAllCables();
                updateConnectedPorts();
            }
            patchSettingsService?.applyPatchSettings(data.settings);
            renderLibrary?.();
            clearHistory();
            
            if (instanceModel.getCount() > 0) {
                requestAnimationFrame(() => fitAllDevices());
            }
            return true;
        }
        
        if (data.devices && data.devices.length > 0) {
            const success = migrateV2ToV3(data);
            if (success) clearHistory();
            return success;
        }
        
        return false;
    } catch (e) {
        console.warn('Failed to load working patch:', e);
        return false;
    }
}

function migrateV2ToV3(data) {
    const templateMap = {};
    
    data.devices.forEach(oldDevice => {
        const migrated = migrateDevice(oldDevice);
        const template = {
            id: generateId(),
            name: migrated.name,
            manufacturer: migrated.manufacturer || '',
            version: migrated.version || '',
            portGroups: migrated.portGroups
        };
        if ('isRouter' in migrated) template.isRouter = !!migrated.isRouter;
        definitionModel.add(template);
        
        const instance = {
            id: migrated.id,
            templateId: template.id,
            position: migrated.position
        };
        instanceModel.add(instance);
        templateMap[migrated.id] = template.id;
    });
    
    if (data.connections) {
        const deviceMap = {};
        data.devices.forEach(d => { deviceMap[d.id] = migrateDevice(d); });
        connectionModel.replaceAll(migrateConnections(data.connections, deviceMap));
    }
    
    instanceModel.forEach(d => renderDevice(d));
    renderAllCables();
    updateConnectedPorts();
    renderLibrary?.();
    autosave();
    
    if (instanceModel.getCount() > 0) {
        requestAnimationFrame(() => fitAllDevices());
    }
    
    return true;
}

// ============================================================================
// Connection Management
// ============================================================================

function resolveGroupIndex(deviceId, groupIdOrIndex, direction) {
    return resolveGroupIndexService(deviceId, groupIdOrIndex, direction, instanceModel, getTemplateForDevice);
}

function createConnection(sourceDeviceId, sourceGroupRef, sourceType, sourceIndex,
                          targetDeviceId, targetGroupRef, targetType, targetIndex, options = {}) {
    const sourceDirection = options.sourceDirection || 'output';
    const targetDirection = options.targetDirection || 'input';
    const sourceGroupIndex = resolveGroupIndex(sourceDeviceId, sourceGroupRef, sourceDirection);
    const targetGroupIndex = resolveGroupIndex(targetDeviceId, targetGroupRef, targetDirection);
    
    let replacedConnections = [];
    if (!options.skipValidation && !options.isInternalRouting) {
        const check = checkConnectionAllowed(
            sourceDeviceId, sourceGroupIndex, sourceIndex, sourceType,
            targetDeviceId, targetGroupIndex, targetIndex
        );
        
        if (check.conflictingConnections.length > 0) {
            replacedConnections = check.conflictingConnections.map(c => deepClone(c));
            for (const conn of check.conflictingConnections) {
                connectionModel.remove(conn.id);
            }
        }
    }
    
    const connection = {
        id: generateId(),
        sourceDeviceId,
        sourceGroupIndex,
        sourcePortType: sourceType,
        sourcePortIndex: sourceIndex,
        targetDeviceId,
        targetGroupIndex,
        targetPortType: targetType,
        targetPortIndex: targetIndex,
        ...(sourceDirection !== 'output' && { sourceDirection }),
        ...(targetDirection !== 'input' && { targetDirection })
    };
    connectionModel.add(connection);
    scheduleAutosave();
    
    eventBus.emit(EVENTS.CONNECTION_ADDED, { connection });
    
    if (!options.skipHistory) {
        const connectionCopy = deepClone(connection);
        const replacedCopies = replacedConnections;
        const wasReplacement = replacedCopies.length > 0;
        
        const { undoConnection, redoConnection } = createConnectionRestoreFunctions(connectionCopy, replacedCopies);
        
        recordAction(
            wasReplacement ? 'replace-connection' : 'add-connection',
            wasReplacement ? 'Replace connection' : 'Add connection',
            undoConnection,
            redoConnection
        );
    }
    
    return { success: true, connection, error: null };
}

// ============================================================================
// Save/Load
// ============================================================================

async function saveState() {
    const data = createPatchData({
        templates: definitionModel.getAll(),
        devices: instanceModel.getAll(),
        connections: connectionModel.getAll()
    });
    
    try {
        const shareString = await toShareString(data);
        const blob = new Blob([shareString], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        
        const a = document.createElement('a');
        a.href = url;
        const filename = (appState.get('patch.name') || 'patchwerk-patch').replace(/[^a-zA-Z0-9-_ ]/g, '_');
        a.download = `${filename}.pw`;
        a.click();
        
        URL.revokeObjectURL(url);
        showToast('Patch exported');
    } catch (e) {
        showToast('Export failed: ' + e.message, true);
    }
}

function loadState() {
    const fileInput = document.getElementById('file-input');
    fileInput?.click();
}

// ============================================================================
// Favorites
// ============================================================================

function toggleFavorite(templateId) {
    const template = definitionModel.getById(templateId);
    favoritesService?.toggle(templateId, template);
    renderLibrary?.();
}

// ============================================================================
// Help Overlay
// ============================================================================

function toggleHelpOverlay() {
    const overlay = document.getElementById('help-overlay');
    if (overlay) overlay.classList.toggle('active');
}

// ============================================================================
// Footer Bar
// ============================================================================

function updateFooterLibraryState(isOpen) {
    const footer = document.getElementById('footer-bar');
    if (footer) {
        if (isOpen) {
            footer.classList.add('library-open');
        } else {
            footer.classList.remove('library-open');
        }
    }
}

// ============================================================================
// Multi-Drag Preview
// ============================================================================

function refreshMultiDragPreview(altKeyPressed) {
    const cableDragState = getCableDragState?.();
    const lastMousePosition = getLastMousePosition?.();
    const lastHoveredTargetPortInfo = getLastHoveredTargetPortInfo?.();
    
    if (!cableDragState?.isMultiDrag || !lastMousePosition) return;
    
    const syntheticEvent = {
        clientX: lastMousePosition.clientX,
        clientY: lastMousePosition.clientY,
        altKey: altKeyPressed
    };
    
    updateTempCable?.(syntheticEvent, lastHoveredTargetPortInfo);
}

// ============================================================================
// Keyboard Shortcut Handlers
// ============================================================================

function handleCopy() {
    if (libraryMultiSelect.selectedItems.size > 0) {
        const templateIds = Array.from(libraryMultiSelect.selectedItems);
        copyLibrarySelectionToClipboard(templateIds).then(success => {
            const count = templateIds.length;
            showToast(success ? `Copied ${count} device${count > 1 ? 's' : ''}!` : 'Could not copy to clipboard');
        });
        return;
    }
    
    if (hasDeviceSelection()) {
        copySelection();
        copySelectionToSystemClipboard().then(success => {
            const devCount = selectionState.devices.size;
            const selClip = getSelectionClipboard();
            const connCount = selClip?.connections?.length || 0;
            const msg = `Copied ${devCount} device${devCount > 1 ? 's' : ''}` +
                (connCount > 0 ? ` + ${connCount} connection${connCount > 1 ? 's' : ''}` : '');
            showToast(success ? msg : msg + ' (local only)');
        });
    } else if (state.selectedTemplateId) {
        copyDeviceToSystemClipboard(state.selectedTemplateId, true).then(success => {
            showToast(success ? 'Device copied!' : 'Could not copy to clipboard');
        });
    }
}

function handleCut() {
    if (hasDeviceSelection()) {
        copySelection();
        copySelectionToSystemClipboard().then(() => {
            const devCount = selectionState.devices.size;
            deleteSelected();
            showToast(`Cut ${devCount} device${devCount > 1 ? 's' : ''}`);
        });
    }
}

function handlePaste() {
    pasteSelectionFromSystemClipboard().then(devices => {
        if (devices && devices.length > 0) {
            const connCount = connectionModel.filter(c =>
                devices.some(d => d.id === c.sourceDeviceId) &&
                devices.some(d => d.id === c.targetDeviceId)
            ).length;
            const msg = `Imported ${devices.length} device${devices.length > 1 ? 's' : ''}` +
                (connCount > 0 ? ` + ${connCount} connection${connCount > 1 ? 's' : ''}` : '');
            showToast(msg);
        } else {
            return pasteDeviceFromSystemClipboard().then(imported => {
                if (imported) {
                    showToast('Device imported!');
                } else if (hasSelectionClipboard()) {
                    const pasted = pasteSelection();
                    if (pasted && pasted.length > 0) {
                        const selClip = getSelectionClipboard();
                        const connCount = selClip?.connections?.length || 0;
                        const msg = `Pasted ${pasted.length} device${pasted.length > 1 ? 's' : ''}` +
                            (connCount > 0 ? ` + ${connCount} connection${connCount > 1 ? 's' : ''}` : '');
                        showToast(msg);
                    }
                } else {
                    const pasted = pasteDevice();
                    if (pasted) {
                        showToast('Device pasted (linked)');
                    } else {
                        showToast('Nothing to paste', true);
                    }
                }
            });
        }
    }).catch(() => {
        if (hasSelectionClipboard()) {
            const pasted = pasteSelection();
            if (pasted && pasted.length > 0) {
                const selClip = getSelectionClipboard();
                const connCount = selClip?.connections?.length || 0;
                const msg = `Pasted ${pasted.length} device${pasted.length > 1 ? 's' : ''}` +
                    (connCount > 0 ? ` + ${connCount} connection${connCount > 1 ? 's' : ''}` : '');
                showToast(msg);
            }
        } else {
            const pasted = pasteDevice();
            if (pasted) {
                showToast('Device pasted (linked)');
            } else {
                showToast('Nothing to paste', true);
            }
        }
    });
}

function handleDuplicate() {
    if (hasDeviceSelection()) {
        if (copySelection()) {
            const pasted = pasteSelection();
            if (pasted && pasted.length > 0) {
                const connCount = getSelectionClipboard()?.connections?.length || 0;
                const msg = `Duplicated ${pasted.length} device${pasted.length > 1 ? 's' : ''}` +
                    (connCount > 0 ? ` + ${connCount} connection${connCount > 1 ? 's' : ''}` : '');
                showToast(msg);
            }
        }
    } else if (libraryMultiSelect.selectedItems.size > 0 || state.selectedTemplateId) {
        duplicateSelectedTemplate?.();
    }
}

// ============================================================================
// File Import Handlers
// ============================================================================

function importFromFile() {
    controllers.patchSwitcherController?.closePatchSwitcher?.();
    const importFileInput = document.getElementById('import-file-input');
    importFileInput?.click();
}

function setupFileImportHandlers() {
    const importFileInput = document.getElementById('import-file-input');
    const fileInput = document.getElementById('file-input');
    
    importFileInput?.addEventListener('change', async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = async (loadEvent) => {
            try {
                const content = loadEvent.target.result.trim();
                
                if (!content.startsWith('PW1~')) {
                    throw new Error('Invalid file format. Expected .pw share string format.');
                }
                
                const data = await fromShareString(content);
                const validation = validatePatchData(data);
                if (!validation.valid) throw new Error(validation.error || 'Invalid patch data');
                if (validation.needsMigration) throw new Error('Patch format too old');
                
                const { patchData: remapped } = remapPatchIds(data);
                
                remapped.templates.forEach(t => definitionModel.add(t));
                remapped.devices.forEach(d => {
                    instanceModel.add(d);
                    renderDevice(d);
                });
                remapped.connections.forEach(c => connectionModel.add(c));
                
                renderAllCables();
                updateConnectedPorts();
                renderLibrary?.();
                autosave();
                showToast('Imported patch from file');
            } catch (err) {
                showToast('Failed to import: ' + err.message, true);
            }
        };
        reader.readAsText(file);
        importFileInput.value = '';
    });
    
    fileInput?.addEventListener('change', async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = async (loadEvent) => {
            try {
                const content = loadEvent.target.result.trim();
                
                if (!content.startsWith('PW1~')) {
                    throw new Error('Invalid file format. Expected .pw share string format.');
                }
                
                const data = await fromShareString(content);
                
                definitionModel.clear();
                instanceModel.clear();
                connectionModel.clear();
                clearDeviceSelection();
                state.selectedConnectionId = null;
                state.selectedTemplateId = null;
                clearHistory();
                canvas.innerHTML = '';
                
                const viewport = getViewport();
                viewport.x = 0;
                viewport.y = 0;
                viewport.scale = 1;
                applyViewportTransform();
                
                if (data.version >= 3 && data.templates) {
                    definitionModel.replaceAll(data.templates);
                    data.devices.forEach(d => {
                        instanceModel.add(d);
                        renderDevice(d);
                    });
                    if (data.connections) {
                        connectionModel.replaceAll(data.connections);
                        renderAllCables();
                        updateConnectedPorts();
                    }
                } else {
                    migrateV2ToV3(data);
                }
                
                renderLibrary?.();
                showToast('Patch loaded');
            } catch (err) {
                showToast('Failed to load patch: ' + err.message, true);
            }
        };
        reader.readAsText(file);
        fileInput.value = '';
    });
}

// ============================================================================
// Application Initialization
// ============================================================================

(async () => {
    // Bootstrap the application
    const bootstrapResult = await bootstrap({
        // Runtime callbacks
        showAutosaveIndicator,
        cableIntersectsBoxGeom,
        getTemplateForDevice,
        updateSelectionButtons,
        renderLibrary: () => renderLibrary?.(),
        createConnection,
        selectDevice,
        scheduleAutosave,
        recordAction,
        renderDevice,
        duplicateSelectedTemplate: () => duplicateSelectedTemplate?.(),
        createInstanceFromTemplate,
        updateFooterLibraryState,
        undo,
        redo,
        loadVersion,
        loadAndApplyPatch,
        setCurrentPatchName,
        autosave,
        createNewPatch,
        newPatch,
        saveState,
        importFromFile,
        editDefinition,
        editInstanceLayout,
        toggleHelpOverlay,
        loadState,
        refreshMultiDragPreview,
        getGroupDisplayName,
        updateUndoRedoButtons,
        clearHistory,
        applyPatchData,
        rerenderAllDevicesForTemplate,
        createDevice,
        handleCopy,
        handleCut,
        handlePaste,
        handleDuplicate
    });
    
    // Populate module-level references from bootstrap result
    state = bootstrapResult.state;
    instanceModel = bootstrapResult.instanceModel;
    definitionModel = bootstrapResult.definitionModel;
    connectionModel = bootstrapResult.connectionModel;
    debugConfig = bootstrapResult.debugConfig;
    selectionState = bootstrapResult.selectionState;
    libraryMultiSelect = bootstrapResult.libraryMultiSelect;
    PORT_CONNECTION_RULES = bootstrapResult.PORT_CONNECTION_RULES;
    
    authService = bootstrapResult.authService;
    storageService = bootstrapResult.storageService;
    favoritesService = bootstrapResult.favoritesService;
    patchSettingsService = bootstrapResult.patchSettingsService;
    
    canvas = bootstrapResult.canvas;
    cablesSvg = bootstrapResult.cablesSvg;
    
    controllers = {
        authUIController: bootstrapResult.authUIController,
        versionHistoryController: bootstrapResult.versionHistoryController,
        storageUIController: bootstrapResult.storageUIController,
        migrationController: bootstrapResult.migrationController,
        libraryController: bootstrapResult.libraryController,
        instanceLayoutController: bootstrapResult.instanceLayoutController,
        shareController: bootstrapResult.shareController,
        patchModalController: bootstrapResult.patchModalController,
        patchSwitcherController: bootstrapResult.patchSwitcherController
    };
    
    // Re-exports
    renderAllCables = bootstrapResult.renderAllCables;
    updateConnectedPorts = bootstrapResult.updateConnectedPorts;
    getViewport = bootstrapResult.getViewport;
    applyViewportTransform = bootstrapResult.applyViewportTransform;
    updateZoomIndicator = bootstrapResult.updateZoomIndicator;
    fitAllDevices = bootstrapResult.fitAllDevices;
    saveViewport = bootstrapResult.saveViewport;
    screenToCanvas = bootstrapResult.screenToCanvas;
    getPortPosition = bootstrapResult.getPortPosition;
    getConnectionSourceGroup = bootstrapResult.getConnectionSourceGroup;
    getConnectionTargetGroup = bootstrapResult.getConnectionTargetGroup;
    clearDeviceSelection = bootstrapResult.clearDeviceSelection;
    setDeviceSelection = bootstrapResult.setDeviceSelection;
    hasDeviceSelection = bootstrapResult.hasDeviceSelection;
    getSingleSelectedDevice = bootstrapResult.getSingleSelectedDevice;
    getSelectedDevices = bootstrapResult.getSelectedDevices;
    updateSelectionVisuals = bootstrapResult.updateSelectionVisuals;
    bringToFront = bootstrapResult.bringToFront;
    showToast = bootstrapResult.showToast;
    showConfirmModal = bootstrapResult.showConfirmModal;
    showAddDeviceModal = bootstrapResult.showAddDeviceModal;
    showEditTemplateModal = bootstrapResult.showEditTemplateModal;
    closeModal = bootstrapResult.closeModal;
    getCableDragState = bootstrapResult.getCableDragState;
    getLastMousePosition = bootstrapResult.getLastMousePosition;
    getLastHoveredTargetPortInfo = bootstrapResult.getLastHoveredTargetPortInfo;
    updateTempCable = bootstrapResult.updateTempCable;
    deleteSelected = bootstrapResult.deleteSelected;
    clearRouting = bootstrapResult.clearRouting;
    copySelection = bootstrapResult.copySelection;
    pasteSelection = bootstrapResult.pasteSelection;
    getSelectionClipboard = bootstrapResult.getSelectionClipboard;
    hasSelectionClipboard = bootstrapResult.hasSelectionClipboard;
    hasClipboardContent = bootstrapResult.hasClipboardContent;
    pasteDevice = bootstrapResult.pasteDevice;
    duplicateDevice = bootstrapResult.duplicateDevice;
    copyDeviceToSystemClipboard = bootstrapResult.copyDeviceToSystemClipboard;
    copyLibrarySelectionToClipboard = bootstrapResult.copyLibrarySelectionToClipboard;
    pasteDeviceFromSystemClipboard = bootstrapResult.pasteDeviceFromSystemClipboard;
    copySelectionToSystemClipboard = bootstrapResult.copySelectionToSystemClipboard;
    pasteSelectionFromSystemClipboard = bootstrapResult.pasteSelectionFromSystemClipboard;
    toggleAccountMenu = bootstrapResult.toggleAccountMenu;
    togglePatchSwitcher = bootstrapResult.togglePatchSwitcher;
    closePatchSwitcher = bootstrapResult.closePatchSwitcher;
    toggleDebugPanel = bootstrapResult.toggleDebugPanel;
    updateDebugStraightDist = bootstrapResult.updateDebugStraightDist;
    updateDebugCableStyle = bootstrapResult.updateDebugCableStyle;
    updatePortRule = bootstrapResult.updatePortRule;
    deletePortType = bootstrapResult.deletePortType;
    exportDebugSettings = bootstrapResult.exportDebugSettings;
    contextMenuAction = bootstrapResult.contextMenuAction;
    
    // Library controller exports
    renderLibrary = bootstrapResult.libraryController.renderLibrary;
    duplicateSelectedTemplate = bootstrapResult.libraryController.duplicateSelectedTemplate;
    
    // Setup file import handlers
    setupFileImportHandlers();
    
    // Check for patch in URL first (shareable link)
    const loadedFromUrl = await controllers.shareController?.loadPatchFromUrl?.();
    
    // If no URL patch, load working patch or create demo
    if (!loadedFromUrl) {
        const loaded = await loadWorkingPatch();
        if (!loaded) {
            createDemoPatch(createTemplate, createInstanceFromTemplate);
        }
    }
    
    // ========================================================================
    // Expose Global Functions for HTML onclick handlers
    // ========================================================================
    
    Object.assign(window, {
        // Debug
        debugFavorites: () => favoritesService?.debug(),
        
        // Account & Auth
        toggleAccountMenu,
        loginWithDiscord: controllers.authUIController?.loginWithDiscord,
        logout: controllers.authUIController?.logout,
        showProfileSettings: controllers.authUIController?.showProfileSettings,
        closeProfileSettings: controllers.authUIController?.closeProfileSettings,
        saveProfileSettings: controllers.authUIController?.saveProfileSettings,
        handleAvatarSourceChange: controllers.authUIController?.handleAvatarSourceChange,
        previewCustomAvatar: controllers.authUIController?.previewCustomAvatar,
        showDeleteAccountModal: controllers.authUIController?.showDeleteAccountModal,
        closeDeleteAccountModal: controllers.authUIController?.closeDeleteAccountModal,
        confirmDeleteAccount: controllers.authUIController?.confirmDeleteAccount,
        
        // Patch Switcher
        togglePatchSwitcher,
        closePatchSwitcher,
        renderPatchSwitcherList: controllers.patchSwitcherController?.renderPatchSwitcherList,
        switchToPatch: controllers.patchSwitcherController?.switchToPatch,
        switchToPatchFromMenu: controllers.patchSwitcherController?.switchToPatchFromMenu,
        deletePatchFromSwitcher: controllers.patchSwitcherController?.deletePatchFromSwitcher,
        newPatchFromSwitcher: controllers.patchSwitcherController?.newPatchFromSwitcher,
        newPatchFromMenu: controllers.patchSwitcherController?.newPatchFromMenu,
        saveAsFromMenu: controllers.patchSwitcherController?.saveAsFromMenu,
        openFromMenu: controllers.patchSwitcherController?.openFromMenu,
        saveVersionFromMenu: controllers.patchSwitcherController?.saveVersionFromMenu,
        showVersionHistoryFromMenu: controllers.patchSwitcherController?.showVersionHistoryFromMenu,
        saveToFileFromMenu: controllers.patchSwitcherController?.saveToFileFromMenu,
        saveToClipboardFromMenu: controllers.patchSwitcherController?.saveToClipboardFromMenu,
        importFromFileFromMenu: controllers.patchSwitcherController?.importFromFileFromMenu,
        importFromClipboardFromMenu: controllers.patchSwitcherController?.importFromClipboardFromMenu,
        exportShareCodeFromMenu: controllers.patchSwitcherController?.exportShareCodeFromMenu,
        importShareCodeFromMenu: controllers.patchSwitcherController?.importShareCodeFromMenu,
        patchSettingsFromMenu: controllers.patchSwitcherController?.patchSettingsFromMenu,
        
        // Patch Modals
        showNewPatchModal: controllers.patchModalController?.showNewPatchModal,
        closeNewPatchModal: controllers.patchModalController?.closeNewPatchModal,
        confirmNewPatch: controllers.patchModalController?.confirmNewPatch,
        showSaveAsModal: controllers.patchModalController?.showSaveAsModal,
        closeSaveAsModal: controllers.patchModalController?.closeSaveAsModal,
        confirmSaveAs: controllers.patchModalController?.confirmSaveAs,
        showPatchSettingsModal: controllers.patchModalController?.showPatchSettingsModal,
        closePatchSettingsModal: controllers.patchModalController?.closePatchSettingsModal,
        savePatchSettingsModal: controllers.patchModalController?.savePatchSettingsModal,
        
        // Version History
        showSaveVersionModal: controllers.versionHistoryController?.showSaveVersionModal,
        closeSaveVersionModal: controllers.versionHistoryController?.closeSaveVersionModal,
        confirmSaveVersion: controllers.versionHistoryController?.confirmSaveVersion,
        showVersionHistoryModal: controllers.versionHistoryController?.showVersionHistoryModal,
        closeVersionHistoryModal: controllers.versionHistoryController?.closeVersionHistoryModal,
        restoreVersionFromHistory: controllers.versionHistoryController?.restoreVersionFromHistory,
        deleteVersionFromHistory: controllers.versionHistoryController?.deleteVersionFromHistory,
        
        // Storage Modals
        saveToStorage: controllers.storageUIController?.saveToStorage,
        renderSaveExistingList: controllers.storageUIController?.renderSaveExistingList,
        selectPatchForSave: controllers.storageUIController?.selectPatchForSave,
        closeSaveStorageModal: controllers.storageUIController?.closeSaveStorageModal,
        confirmSaveToStorage: controllers.storageUIController?.confirmSaveToStorage,
        openFromStorage: controllers.storageUIController?.openFromStorage,
        importFromStorage: controllers.storageUIController?.importFromStorage,
        showOpenStorageModal: controllers.storageUIController?.showOpenStorageModal,
        filterOpenPatchList: controllers.storageUIController?.filterOpenPatchList,
        renderOpenPatchList: controllers.storageUIController?.renderOpenPatchList,
        openPatchFromList: controllers.storageUIController?.openPatchFromList,
        deletePatchFromList: controllers.storageUIController?.deletePatchFromList,
        closeOpenStorageModal: controllers.storageUIController?.closeOpenStorageModal,
        
        // Migration
        closeMigrationModal: controllers.migrationController?.closeMigrationModal,
        migratePatches: controllers.migrationController?.migratePatches,
        
        // Library
        setLibraryTab: controllers.libraryController?.setLibraryTab,
        handleLibraryItemClick: controllers.libraryController?.handleLibraryItemClick,
        handleFavoriteItemClick: controllers.libraryController?.handleFavoriteItemClick,
        handleGlobalItemClick: controllers.libraryController?.handleGlobalItemClick,
        handleLibraryDragStart: controllers.libraryController?.handleLibraryDragStart,
        handleLibraryDragEnd: controllers.libraryController?.handleLibraryDragEnd,
        toggleLibrary: controllers.libraryController?.toggleLibrary,
        filterLibrary: controllers.libraryController?.filterLibrary,
        editTemplate: controllers.libraryController?.editTemplate,
        editSelectedTemplate: controllers.libraryController?.editSelectedTemplate,
        deleteSelectedTemplate: controllers.libraryController?.deleteSelectedTemplate,
        duplicateSelectedTemplate: controllers.libraryController?.duplicateSelectedTemplate,
        addGlobalDeviceToFavorites: controllers.libraryController?.addGlobalDeviceToFavorites,
        addGlobalDeviceToPatch: controllers.libraryController?.addGlobalDeviceToPatch,
        // Global tab methods
        handleManufacturerInput: controllers.libraryController?.handleManufacturerInput,
        showManufacturerDropdown: controllers.libraryController?.showManufacturerDropdown,
        hideManufacturerDropdownDelayed: controllers.libraryController?.hideManufacturerDropdownDelayed,
        selectManufacturer: controllers.libraryController?.selectManufacturer,
        handleGlobalDeviceSearch: controllers.libraryController?.handleGlobalDeviceSearch,
        editDefinition,
        editInstanceLayout,
        toggleFavorite,
        exportSelectedDevice: bootstrapResult.exportSelectedDevice,
        importDeviceFromFile: bootstrapResult.importDeviceFromFile,
        copySelectedToClipboard: bootstrapResult.copySelectedToClipboard,
        
        // Device Modal
        showAddDeviceModal,
        closeModal,
        saveDevice,
        saveDeviceAsNew,
        addPortGroupRow,
        removePortGroup,
        updatePortGroup,
        toggleGroupAdvanced,
        showMultiTypeEditor,
        closeMultiTypeEditor,
        applyMultiTypeEditor,
        applyMultiTypePreset,
        toggleMultiType,
        
        // Layout Modal
        closeLayoutModal: controllers.instanceLayoutController?.closeLayoutModal,
        saveInstanceLayout: controllers.instanceLayoutController?.saveInstanceLayout,
        resetInstanceLayout: controllers.instanceLayoutController?.resetInstanceLayout,
        setGroupSide: controllers.instanceLayoutController?.setGroupSide,
        setGroupColor: controllers.instanceLayoutController?.setGroupColor,
        setGroupSplit: controllers.instanceLayoutController?.setGroupSplit,
        toggleColorPicker: controllers.instanceLayoutController?.toggleColorPicker,
        toggleClockwisePortOrder: controllers.instanceLayoutController?.toggleClockwisePortOrder,
        
        // Canvas Operations
        foldGroupInline: controllers.instanceLayoutController?.foldGroupInline,
        unfoldGroup: controllers.instanceLayoutController?.unfoldGroup,
        duplicateSelectedDevice: bootstrapResult.duplicateSelectedDevice,
        deleteSelected,
        clearRouting,
        pasteFromClipboard: bootstrapResult.pasteFromClipboard,
        
        // Share & Clipboard
        copyPatchShareString: controllers.shareController?.copyPatchShareString,
        copySelectionShareString: controllers.shareController?.copySelectionShareString,
        showImportShareStringDialog: controllers.shareController?.showImportShareStringDialog,
        closeShareStringModal: controllers.shareController?.closeShareStringModal,
        importShareStringFromModal: controllers.shareController?.importShareStringFromModal,
        importPatchFromShareString: controllers.shareController?.importPatchFromShareString,
        saveToClipboard: controllers.shareController?.saveToClipboard,
        openFromClipboard: controllers.shareController?.openFromClipboard,
        importFromClipboard: controllers.shareController?.importFromClipboard,
        copyClipboardJson: controllers.shareController?.copyClipboardJson,
        closeClipboardModal: controllers.shareController?.closeClipboardModal,
        
        // History
        undo,
        redo,
        
        // Debug
        toggleDebugPanel,
        updateDebugStraightDist,
        updateDebugCableStyle,
        updatePortRule,
        deletePortType,
        exportDebugSettings,
        
        // Help
        toggleHelpOverlay,
        
        // Context Menu
        contextMenuAction
    });
})();
